import React, { useState } from 'react';
import { TrackRankDialog } from './TrackRankDialog';
import { Box, Tooltip } from '@mui/material';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { TrackRankPill as Pill } from '../services/api';

const STAT_LABEL: Record<Pill['stat'], string> = {
  rating: 'driver rating',
  running: 'running position',
  finish: 'avg finish',
};

// Short stat names for the pill itself (the tooltip and dialog use the full ones).
const STAT_SHORT: Record<Pill['stat'], string> = {
  rating: 'rating',
  running: 'avg run',
  finish: 'avg finish',
};

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const valueText = (p: Pill) =>
  p.stat === 'rating'
    ? `${p.value.toFixed(1)} rating`
    : p.stat === 'running'
      ? `average running position ${p.value.toFixed(1)}`
      : `average finish ${p.value.toFixed(1)}`;

/** "2nd best · rating" (green, ▲) or "3rd worst · avg finish" (orange, ▼): this track's rank among
 * the driver's tracks. The tooltip gives the detail; clicking opens the driver's tracks in a dialog. */
export const TrackRankPill: React.FC<{ pill: Pill; site: string; since: number; compact?: boolean }> = ({
  pill,
  site,
  since,
  compact,
}) => {
  const [open, setOpen] = useState(false);
  const best = pill.kind === 'best';
  const color = best ? 'success.main' : 'warning.main';
  const label = `${pill.rank === 1 ? (best ? 'Best' : 'Worst') : `${ordinal(pill.rank)} ${best ? 'best' : 'worst'}`} · ${STAT_SHORT[pill.stat]}`;
  const lastName = pill.driver
    .replace(/,? Jr\.?$/, '')
    .split(' ')
    .pop();
  const tip =
    `Of ${lastName}'s ${pill.of} tracks since ${since}, ${site} ranks ${pill.rank === 1 ? '' : `${ordinal(pill.rank)} `}` +
    `${best ? 'best' : 'worst'} by ${STAT_LABEL[pill.stat]} (${valueText(pill)}, ${pill.races} races here). Click for their tracks.`;
  const Icon = best ? TrendingUp : TrendingDown;
  return (
    <>
      <Tooltip title={tip} enterTouchDelay={0}>
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={tip}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              setOpen(true);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            px: 0.75,
            py: compact ? 0 : 0.1,
            borderRadius: 999,
            border: 1,
            borderColor: color,
            color,
            fontSize: compact ? 10 : 10.5,
            fontWeight: 600,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Icon sx={{ fontSize: compact ? 12 : 13 }} />
          {label}
        </Box>
      </Tooltip>
      {open && <TrackRankDialog pill={pill} site={site} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};
